/**
 * ZenMarkup Privacy Analytics — Line Chart drawing helpers.
 *
 * Grid, line, gradient fill, empty state, Y-axis formatting.
 * Extracted from line-chart.js to keep modules under 200 lines.
 */


const GRID_COLOR = '#E2E8F0', LABEL_COLOR = '#64748B';
export const AXIS_FONT = '11px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
/** Gap between label right edge and grid line start (px). */
export const LABEL_GAP = 8;

export const LABEL_FONT = '12px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

/**
 * Round a max value to a "nice" ceiling for the Y axis.
 *
 * @param {number} max         Raw maximum value.
 * @param {string} activeSeries Current metric key.
 * @return {number}
 */
export function niceMax(max, activeSeries) {
	if (activeSeries === 'bounce_rate') {
		if (max <= 10) return 10;
		if (max <= 25) return 25;
		if (max <= 50) return 50;
		return 100;
	}
	if (max <= 5) return 5;
	const mag = Math.pow(10, Math.floor(Math.log10(max)));
	const r = max / mag;
	if (r <= 1.5) return Math.ceil(1.5 * mag);
	if (r <= 3) return Math.ceil(3 * mag);
	if (r <= 7) return Math.ceil(7 * mag);
	return Math.ceil(10 * mag);
}

/**
 * Format a Y-axis value based on active series.
 *
 * @param {number} val
 * @param {string} activeSeries
 * @return {string}
 */
export function formatYValue(val, activeSeries) {
	if (activeSeries === 'bounce_rate') {
		return val.toFixed(0) + '%';
	}
	if (activeSeries === 'avg_duration') {
		var s = Math.max(0, Math.round(val));
		var m = Math.floor(s / 60);
		var sec = s % 60;
		return m + ':' + (sec < 10 ? '0' : '') + sec;
	}
	return val.toLocaleString();
}

/**
 * Draw horizontal grid lines and Y-axis labels.
 *
 * @param {Object} opts  { ctx, w, h, values, PAD, activeSeries }
 */
export function drawGrid(opts) {
	const ctx = opts.ctx, w = opts.w, h = opts.h;
	const PAD = opts.PAD, values = opts.values;
	const maxVal = niceMax(Math.max.apply(null, values), opts.activeSeries);
	const steps = 5, chartH = h - PAD.top - PAD.bottom;
	ctx.clearRect(0, 0, w, h);
	ctx.font = AXIS_FONT;
	ctx.textAlign = 'right';
	ctx.textBaseline = 'middle';
	for (let i = 0; i <= steps; i++) {
		const val = (maxVal / steps) * i;
		const y = PAD.top + chartH - (chartH * (i / steps));
		ctx.strokeStyle = GRID_COLOR;
		ctx.lineWidth = 1;
		ctx.setLineDash([4, 4]);
		ctx.beginPath();
		ctx.moveTo(PAD.left, y);
		ctx.lineTo(w - PAD.right, y);
		ctx.stroke();
		ctx.setLineDash([]);
		ctx.fillStyle = LABEL_COLOR;
		ctx.fillText(formatYValue(val, opts.activeSeries), PAD.left - LABEL_GAP, y);
	}
}

/**
 * Draw the data line with gradient fill.
 *
 * @param {Object} opts  { ctx, w, h, values, PAD, activeSeries, progress, lineColor }
 */
export function drawLine(opts) {
	const ctx = opts.ctx, w = opts.w, h = opts.h;
	const PAD = opts.PAD, values = opts.values;
	const maxVal = niceMax(Math.max.apply(null, values), opts.activeSeries);
	const chartW = w - PAD.left - PAD.right;
	const chartH = h - PAD.top - PAD.bottom;
	const len = values.length, stepX = len > 1 ? chartW / (len - 1) : 0;
	const points = values.map(function (v, i) {
		return { x: PAD.left + i * stepX, y: PAD.top + chartH - (chartH * (v / maxVal)) };
	});
	var useClip = typeof opts.progress === 'number' && opts.progress < 1;
	if (useClip) {
		ctx.save();
		ctx.beginPath();
		ctx.rect(0, 0, PAD.left + chartW * opts.progress, h);
		ctx.clip();
	}
	var grad = ctx.createLinearGradient(0, PAD.top, 0, h - PAD.bottom);
	grad.addColorStop(0, 'rgba(37, 99, 235, 0.25)');
	grad.addColorStop(1, 'rgba(37, 99, 235, 0)');
	ctx.beginPath();
	ctx.moveTo(points[0].x, points[0].y);
	for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
	ctx.lineTo(points[points.length - 1].x, h - PAD.bottom);
	ctx.lineTo(points[0].x, h - PAD.bottom);
	ctx.closePath();
	ctx.fillStyle = grad;
	ctx.fill();
	ctx.beginPath();
	ctx.moveTo(points[0].x, points[0].y);
	for (let j = 1; j < points.length; j++) ctx.lineTo(points[j].x, points[j].y);
	ctx.strokeStyle = opts.lineColor;
	ctx.lineWidth = 2;
	ctx.lineJoin = 'round';
	ctx.stroke();
	if (useClip) {
		ctx.restore();
	}
}

/**
 * Draw empty state placeholder when no data.
 *
 * @param {Object} opts  { ctx, w, h, PAD, activeSeries, drawXAxis }
 */
export function drawEmptyState(opts) {
	const ctx = opts.ctx, w = opts.w, h = opts.h, PAD = opts.PAD;
	const chartH = h - PAD.top - PAD.bottom;
	const steps = 5;
	ctx.clearRect(0, 0, w, h);
	ctx.font = AXIS_FONT;
	ctx.textAlign = 'right';
	ctx.textBaseline = 'middle';
	for (let i = 0; i <= steps; i++) {
		const y = PAD.top + chartH - (chartH * (i / steps));
		ctx.strokeStyle = GRID_COLOR;
		ctx.lineWidth = 1;
		ctx.setLineDash([4, 4]);
		ctx.beginPath();
		ctx.moveTo(PAD.left, y);
		ctx.lineTo(w - PAD.right, y);
		ctx.stroke();
		ctx.setLineDash([]);
		ctx.fillStyle = LABEL_COLOR;
		ctx.fillText(formatYValue(i * 2, opts.activeSeries), PAD.left - LABEL_GAP, y);
	}
	opts.drawXAxis(w, h);
	ctx.fillStyle = '#94A3B8';
	ctx.font = LABEL_FONT;
	ctx.textAlign = 'center';
	ctx.textBaseline = 'middle';
	ctx.fillText('No data for this period', w / 2, PAD.top + chartH / 2);
}
